import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Content, Divider, Flex, FlexItem, Label, Spinner, Title } from '@patternfly/react-core';
import { useApiFetch } from '../../api';
import { useNotifications } from '../../contexts/NotificationContext';
import { useUserContext } from '../../contexts/user/UserContext';
import { useImpersonation } from '../../contexts/impersonation/ImpersonationContext';
import { chargeInvoiceAutopay, useAdminInvoiceAutopay } from '../../hooks/useAdminInvoiceAutopay';
import type { AdminInvoiceAutopayInvoice, AdminInvoiceAutopayPlan } from '../../types/models/Wallet';
import type { Invoice } from '../../types/models/Invoice';
import { formatPrice } from '../../utils/money';
import {
  AUTOPAY_OUTCOME_LABEL,
  AUTOPAY_OUTCOME_VARIANT,
  NOTICE_SR_TITLE,
  cardText,
} from './Billing/invoiceAutopayDisplay';

/** Why a row cannot be charged, as the alert title. */
const BLOCKED_PLAN_TITLE: Record<Exclude<AdminInvoiceAutopayPlan, 'wallet' | 'card'>, string> = {
  declined: 'The card was already declined for this invoice',
  no_funds: 'No funds: the balance does not cover it and there is no usable card',
  busy: 'A payment for this invoice is already in progress',
  refused: 'This invoice cannot be collected',
};

interface StaffAutopayChargeProps {
  invoice: Invoice;
  isOpen: boolean;
  /** The customer's own pay flow is submitting — the staff action waits for it. */
  isDisabled: boolean;
  /** Reports a pending charge, so the modal locks its own buttons and its close. */
  onBusyChange: (busy: boolean) => void;
  onPaid: () => void;
  onClose: () => void;
}

/**
 * The staff section of the Pay-invoice modal (docs/api-types/admin-invoice-autopay.md): inside an
 * impersonation session, staff can collect THIS invoice on the organization's own consent — its opt-in
 * to the automatic payment of all its invoices — through the staff-only charge route, which records the
 * staff user as the person who charged it. It is not the customer's pay flow (whose wallet / card
 * sources stay refused under impersonation) and never pays "as" the customer.
 *
 * Renders nothing for anyone but staff inside an impersonation session, or while the backend does not
 * serve the route (HTTP 404).
 */
const StaffAutopayCharge: React.FunctionComponent<StaffAutopayChargeProps> = ({
  invoice,
  isOpen,
  isDisabled,
  onBusyChange,
  onPaid,
  onClose,
}) => {
  const user = useUserContext();
  const { target, isImpersonating } = useImpersonation();
  // useUserContext() is the real (staff) user during an impersonation session.
  const clientId = user?.is_ancalagon_staff === true && isImpersonating && target !== null ? target.id : null;
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { preview, available, loading, error, refetch } = useAdminInvoiceAutopay(clientId, isOpen && clientId !== null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isCharging, setIsCharging] = useState(false);
  const [result, setResult] = useState<{ variant: 'warning' | 'danger'; message: string } | null>(null);

  useEffect(() => {
    setIsConfirming(false);
    setResult(null);
  }, [isOpen, invoice.id]);

  const charge = useCallback(async () => {
    if (clientId === null) {
      return;
    }
    setIsCharging(true);
    onBusyChange(true);
    setResult(null);
    const response = await chargeInvoiceAutopay(apiFetch, clientId, invoice.id);
    // Release the modal BEFORE onPaid / onClose: this block unmounts with the modal body.
    setIsCharging(false);
    setIsConfirming(false);
    onBusyChange(false);

    if (response.kind === 'outcome') {
      const { outcome, message } = response.result;
      const variant = AUTOPAY_OUTCOME_VARIANT[outcome];
      addNotification({
        title: `Invoice #${invoice.numero}: ${AUTOPAY_OUTCOME_LABEL[outcome]}`,
        srTitle: NOTICE_SR_TITLE[variant],
        description: message,
        variant,
      });
      setDrawerOpen(true);
      if (outcome === 'paid' || outcome === 'in_flight') {
        onPaid();
        onClose();
        return;
      }
      setResult({ variant: variant === 'danger' ? 'danger' : 'warning', message });
      void refetch();
      return;
    }

    const message =
      response.kind === 'error' ? response.message : 'The staff charge is not available on this backend yet.';
    addNotification({
      title: `Could not charge invoice #${invoice.numero}`,
      srTitle: NOTICE_SR_TITLE.danger,
      description: message,
      variant: 'danger',
    });
    setDrawerOpen(true);
    setResult({ variant: 'danger', message });
    void refetch();
  }, [apiFetch, clientId, invoice, onBusyChange, onPaid, onClose, addNotification, setDrawerOpen, refetch]);

  if (clientId === null || !available) {
    return null;
  }

  const renderChargeControl = (row: AdminInvoiceAutopayInvoice & { plan: 'wallet' | 'card'; amount: string }) => {
    const amount = formatPrice(row.amount);
    if (isConfirming) {
      return (
        <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsSm' }}>
          <FlexItem>
            <Content component="p">
              <strong>Charge {amount} now?</strong>
            </Content>
          </FlexItem>
          <FlexItem>
            <Flex spaceItems={{ default: 'spaceItemsSm' }}>
              <FlexItem>
                <Button
                  variant="danger"
                  onClick={() => void charge()}
                  isLoading={isCharging}
                  isDisabled={isCharging || isDisabled}
                >
                  Confirm charge
                </Button>
              </FlexItem>
              <FlexItem>
                <Button variant="link" onClick={() => setIsConfirming(false)} isDisabled={isCharging}>
                  Cancel
                </Button>
              </FlexItem>
            </Flex>
          </FlexItem>
        </Flex>
      );
    }
    const label =
      row.plan === 'wallet'
        ? `Charge ${amount} from the prepaid balance`
        : `Charge ${amount} to ${preview?.card ? cardText(preview.card) : 'the saved card'}`;
    return (
      <Button variant="secondary" onClick={() => setIsConfirming(true)} isDisabled={isDisabled}>
        {label}
      </Button>
    );
  };

  const renderState = () => {
    if (loading && preview === null) {
      return <Spinner size="md" aria-label="Loading the organization's unpaid invoices" />;
    }
    if (error && preview === null) {
      return (
        <Alert
          variant="danger"
          isInline
          title={error}
          actionLinks={
            <Button variant="link" isInline onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      );
    }
    if (preview === null) {
      return null;
    }
    if (!preview.invoice_autopay.enabled) {
      return (
        <Alert variant="info" isInline title="Automatic payment is off for this organization">
          Staff can charge an invoice only when the organization itself has turned on automatic payment of its invoices.
        </Alert>
      );
    }
    const row = preview.invoices.find((r) => r.invoice_id === invoice.id);
    if (!row) {
      return (
        <Alert
          variant="info"
          isInline
          title="This invoice is not in the organization's unpaid invoices any more"
          actionLinks={
            <Button variant="link" isInline onClick={() => void refetch()}>
              Refresh
            </Button>
          }
        />
      );
    }
    if ((row.plan === 'wallet' || row.plan === 'card') && row.amount !== null) {
      return renderChargeControl({ ...row, plan: row.plan, amount: row.amount });
    }
    if (row.plan === 'wallet' || row.plan === 'card') {
      // A payable plan always carries an amount; without one there is nothing safe to show.
      return null;
    }
    return (
      <Alert
        variant="warning"
        isInline
        title={row.plan === 'refused' && row.refusal ? row.refusal.message : BLOCKED_PLAN_TITLE[row.plan]}
      />
    );
  };

  return (
    <>
      <Divider />
      <FlexItem>
        <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
          <FlexItem>
            <Title headingLevel="h3" size="md">
              Staff action — charge on the customer&apos;s consent
            </Title>
          </FlexItem>
          <FlexItem>
            <Label isCompact color="purple">
              Staff only
            </Label>
          </FlexItem>
        </Flex>
        {/* Not claimed while the consent is known to be off: the info alert below says so instead. */}
        {preview?.invoice_autopay.enabled !== false && (
          <Content component="p" className="pf-v6-u-mt-sm">
            The organization turned on automatic payment of its invoices. You can collect this invoice on that consent:
            you are recorded as the person who charged it. This is not paying as the customer.
          </Content>
        )}
        <div className="pf-v6-u-mt-sm">{renderState()}</div>
        {result && <Alert variant={result.variant} isInline title={result.message} className="pf-v6-u-mt-sm" />}
      </FlexItem>
    </>
  );
};

export default StaffAutopayCharge;
